import React, {useCallback, useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';
import {useNavigate, useParams} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import omit from 'lodash.omit';
import {AiTourProvider} from 'src/modules/ai';
import {cleanChangeRequestReview} from 'src/modules/ai/change-request-review/services/actions';
import {stepsByMode} from 'src/modules/ai/tour/steps/steps.config';
import {getChangeRequests} from 'src/modules/change-requests/services';
import {getEventInfo} from 'src/modules/event/services';
import {cleanEventInfo} from 'src/modules/event/services/actions';
import {EventCreateView} from 'src/modules/event-create/event-create.view';
import {createEventCreationRoutePath} from 'src/modules/event-create/event-creation-route-planner';
import {
    useEventTriggerAttachment,
} from 'src/modules/event-create/header-block/event-trigger-attachment/hooks/useEventTriggerAttachment';
import {clearTriggerAttachments} from 'src/modules/event-create/header-block/event-trigger-attachment/services/actions';
import {useDecorationTemplates} from 'src/modules/event-create/hooks/useDecorationTemplates';
import {useDefaultDueDate} from 'src/modules/event-create/hooks/useDefaultDueDate';
import {useTemplate} from 'src/modules/event-create/hooks/useTemplate';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {cleanChangeRequestInfo} from 'src/modules/event-create/services/actions';
import {HeaderBlock, UpdateChangeRequest, UpdateChangeRequestType} from 'src/modules/event-create/typedefs';
import {getPageTitle} from 'src/modules/event-create/utils/getPageTitle';
import {removeEmptyAttributes} from 'src/modules/event-create/utils/removeEmptyAttributes';
import {SuccessNotification} from 'src/modules/event-review/constants';
import {createEventReviewRoutePath} from 'src/modules/event-review/event-review-route-planner';
import {getEventsInfo} from 'src/modules/events/services';
import {
    getAllChangeRequests,
    getChangeRequestById,
    getChangeRequestByIdSuccess,
    getChangeRequestCount,
    saveChangeRequest,
    updateChangeRequest,
} from 'src/openapi/apis/ChangeRequestControllerApi';
import {sendToReview} from 'src/openapi/apis/ChangeRequestStatusControllerApi';
import {getAllEvents, getEventById, getEventByIdSuccess} from 'src/openapi/apis/EventControllerApi';
import {AttributeChangeRequest} from 'src/openapi/models/AttributeChangeRequest';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {DecorationType} from 'src/openapi/models/DecorationType';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch, useAppSelector} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Loading} from '@pb/kit-component-loading';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import {LocalStorage} from '@pb/kit-util-client-storage';

import {DEFAULT_EVENT_CREATION_INFO, EventCreationMode, PushNotificationIds} from './constants';
import {eventCodeInfoToEventChangeRequest} from './utils/eventCodeInfoToEventChangeRequest';
import {changeRequestValidation} from './validations/change-request-validation';

export type Params = {
    eventId: string,
    changeRequestId: string,
};

type Props = {
    mode: EventCreationMode,
};

const SUCCESS_AUTO_HIDE_TIMEOUT = 2000;
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventCreateController = ({mode}: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userEmail = LocalStorage.get('user')?.email || '';
    const eventId = parseInt(useParams<Params>().eventId || '');
    const changeRequestUpdateId = parseInt(useParams<Params>().changeRequestId || '');
    const changeRequestInfo = useAppSelector(getChangeRequestInfo)?.[changeRequestUpdateId];
    const eventCodeInfo = useAppSelector(getEventInfo)?.[eventId];
    const eventsInfo = useAppSelector(getEventsInfo);
    const changeRequests = useAppSelector(getChangeRequests);
    const {triggerAttachments} = useEventTriggerAttachment();
    const {onCreateNote} = usePushNotification();

    const [changeRequestId, setChangeRequestId] = useState<number | null>(null);
    const [eventSave, setEventSave] = useState<EventChangeRequest>(DEFAULT_EVENT_CREATION_INFO);
    const {isLoadingAttributes, template, setTemplate} = useTemplate(mode, eventSave, changeRequestUpdateId, eventId);
    const [validationHighLight, setValidationHighLight] = useState(false);
    const [isEventMarkedDeprecated, setIsEventMarkedDeprecated] = useState(false);
    const defaultDueDate = useDefaultDueDate(changeRequestInfo, changeRequestUpdateId);
    const [dueDate, setDueDate] = useState<Date | undefined>();
    const [decorationType, setDecorationType] = useState<DecorationType | null>();
    const [contextualLinks, setContextualLinks] = useState<ChangeRequestContextualLink[]>([]);
    const decorationTemplate = useDecorationTemplates(eventSave, decorationType);

    const pageTitle = useMemo(() =>
        getPageTitle(eventCodeInfo, changeRequestInfo), [eventCodeInfo, changeRequestInfo]);

    const eventChangeRequest = useMemo(() => {
        if ((eventId && eventCodeInfo || !eventId && changeRequestInfo) && changeRequestUpdateId) {

            return {
                ...changeRequestInfo?.event,
                source: eventSave.source && changeRequestInfo?.event.source !== eventSave.source ?
                    eventSave.source : changeRequestInfo?.event.source || '',
            };
        }

        if (eventId && !changeRequestUpdateId && eventCodeInfo) {
            setTemplate([]);
            setEventSave(eventCodeInfoToEventChangeRequest(eventCodeInfo));
            return eventCodeInfoToEventChangeRequest(eventCodeInfo);
        }
        setTemplate([]);
        setChangeRequestId(null);
        setEventSave(DEFAULT_EVENT_CREATION_INFO);
        return DEFAULT_EVENT_CREATION_INFO;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventCodeInfo, changeRequestInfo]);

    const resetData = useCallback(() => {
        setChangeRequestId(null);
        setTemplate([]);
        setEventSave(DEFAULT_EVENT_CREATION_INFO);
        dispatch(cleanChangeRequestInfo());
        dispatch(cleanEventInfo());
        dispatch(clearTriggerAttachments());
    }, [dispatch, setTemplate]);

    useEffect(() => {
        resetData();
    }, [mode, eventId, changeRequestUpdateId, resetData]);

    useEffect(() => {
        if (isEmpty(eventsInfo)) {
            dispatch(getAllEvents());
        }

        if (isEmpty(changeRequests)) {
            dispatch(getAllChangeRequests());
        }

        if (eventId) {
            dispatch(getEventById({id: eventId})).then(e => {
                if (!e.payload.error && e.type === getEventByIdSuccess.type && !changeRequestUpdateId) {
                    setEventSave(eventCodeInfoToEventChangeRequest(e.payload.data));
                }
            });
        }
        if (changeRequestUpdateId) {
            dispatch(getChangeRequestById({id: changeRequestUpdateId})).then(e => {
                if (!e.payload.error && e.type === getChangeRequestByIdSuccess.type) {
                    setChangeRequestId(e.payload.data.id || null);
                    setEventSave(e.payload.data.event);
                }
            });
        }
        return () => {
            dispatch(cleanChangeRequestInfo());
            dispatch(cleanEventInfo());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, eventId, changeRequestUpdateId]);

    useEffect(() => {
        if (!changeRequestValidation(mode, eventSave, contextualLinks, triggerAttachments).length) {
            setValidationHighLight(false);
        }
    }, [eventSave, contextualLinks, triggerAttachments, mode]);

    const onUpdateChangeRequest = (value: UpdateChangeRequest, type: UpdateChangeRequestType) => {
        if (type === UpdateChangeRequestType.HEADER) {
            const header = value as HeaderBlock;
            setEventSave({...eventSave, ...header});
            setIsEventMarkedDeprecated(header.deprecated);
        }

        if (type === UpdateChangeRequestType.ATTRIBUTES) {
            setEventSave({...eventSave, attributes: value as AttributeChangeRequest[]});
        }
    };

    const onChangeDueDate = (date?: Date) => {
        setDueDate(date ?? undefined);
    };

    const onChangeDecorationType = (value: DecorationType | null) => {
        setDecorationType(value);
    };

    const onChangeContextualLinks = (value: ChangeRequestContextualLink[]) => {
        setContextualLinks(value);
    };

    const saveOrUpdateChangeRequest = async () => {
        const changeRequest = removeEmptyAttributes(eventSave);

        if (changeRequestValidation(mode, changeRequest, contextualLinks, triggerAttachments).length) {
            changeRequestValidation(mode, changeRequest, contextualLinks, triggerAttachments).forEach(error => {
                onCreateNote({
                    id: error,
                    status: Status.ERROR,
                    message: error,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                });
            });
            setValidationHighLight(true);
            return;
        }

        if (changeRequestId) {
            const changeRequestToUpdate = {
                id: changeRequestId,
                event: changeRequest.id ? changeRequest : omit(changeRequest, 'id'),
                authorEmail: userEmail,
                reviewerEmail: changeRequestInfo?.reviewerEmail,
                dueDate: (dueDate || defaultDueDate)?.toISOString(),
                contextualLinks,
                triggerAttachments,
                status: ChangeRequestStatus.NEW,
            };

            const response =
                await dispatch(updateChangeRequest({changeRequest: changeRequestToUpdate}));

            if (response.payload.success) {
                setValidationHighLight(false);
                dispatch(getAllChangeRequests());
                dispatch(cleanChangeRequestReview());
                onCreateNote({
                    id: PushNotificationIds.updateChangeRequestSuccess,
                    status: Status.SUCCESS,
                    message: 'Saved',
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
                });
            }

            if (response.payload.error) {
                onCreateNote({
                    id: PushNotificationIds.updateChangeRequestFailure,
                    status: Status.ERROR,
                    message: response.payload.data.message,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                });
            }

            return;
        }

        const changeRequestToSave = {
            event: changeRequest.id ? changeRequest : omit(changeRequest, 'id'),
            authorEmail: userEmail,
            dueDate: (dueDate || defaultDueDate)?.toISOString(),
            status: ChangeRequestStatus.NEW,
            contextualLinks,
            triggerAttachments,
        };

        const response =
            await dispatch(saveChangeRequest({changeRequest: changeRequestToSave}));

        if (response.payload.success) {
            const id = response.payload.data;
            const path = createEventCreationRoutePath({mode, eventId: eventId || 0, changeRequestId: id});
            window.history.replaceState(window.history.state, '', path);
            setChangeRequestId(id);
            setValidationHighLight(false);
            dispatch(getAllChangeRequests());
            dispatch(getChangeRequestCount());
            dispatch(cleanChangeRequestReview());
            onCreateNote({
                id: PushNotificationIds.saveChangeRequestSuccess,
                status: Status.SUCCESS,
                message: 'Saved',
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.saveChangeRequestFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onSave = () => {
        saveOrUpdateChangeRequest();
    };

    const onSendToReview = async () => {
        if (changeRequestId) {
            await saveOrUpdateChangeRequest();

            const response = await dispatch(sendToReview({changeRequestId}));

            if (response.payload.success) {
                onCreateNote({
                    id: PushNotificationIds.sendToReviewSuccess,
                    status: Status.SUCCESS,
                    message: SuccessNotification[ChangeRequestStatus.WAITING_REVIEW],
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
                });
                dispatch(getAllChangeRequests());
                const eventReviewRoutePath = createEventReviewRoutePath({changeRequestId});

                navigate(eventReviewRoutePath);
            }

            if (response.payload.error) {
                onCreateNote({
                    id: PushNotificationIds.sendToReviewFailure,
                    status: Status.ERROR,
                    message: response.payload.data.message,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                });
            }

            return;
        }

        await onCreateNote({
            id: PushNotificationIds.notSavedChangeRequest,
            status: Status.ERROR,
            message: 'Not saved change request. Please save before send to review',
            buttonProps: {iconProps: {type: Icons.CLOSE}},
            autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
        });
    };

    if (!eventCodeInfo && mode === EventCreationMode.update || !eventsInfo ||
        !changeRequestInfo && changeRequestUpdateId) {
        return <Loading fast={true}/>;
    }

    return (
        <>
            <DocumentTitle title={pageTitle}/>
            <AiTourProvider steps={stepsByMode[mode]}>
                <EventCreateView
                    updateEventInfo={eventChangeRequest}
                    eventChangeRequest={eventSave}
                    {...{
                        mode,
                        validationHighLight,
                        isLoadingAttributes,
                        isEventMarkedDeprecated,
                        decorationTemplate,
                        template,
                        changeRequestId,
                        defaultDueDate,
                        onChangeContextualLinks,
                        onSave,
                        onSendToReview,
                        onUpdateChangeRequest,
                        onChangeDueDate,
                        onChangeDecorationType,
                    }}
                />
            </AiTourProvider>
        </>
    );
};
